// ChangePassword component
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api';

const ChangePassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    // Validation
    if (!formData.currentPassword) {
      setError(t('auth.changePassword.currentPasswordRequired', 'Current password is required'));
      return;
    }

    if (!formData.newPassword) {
      setError(t('auth.changePassword.newPasswordRequired', 'New password is required'));
      return;
    }

    if (formData.newPassword.length < 6) {
      setError(t('auth.changePassword.passwordTooShort', 'Password must be at least 6 characters'));
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('auth.changePassword.passwordMismatch', 'Passwords do not match'));
      return;
    }

    setLoading(true);
    try {
      await apiService.changePassword(formData.currentPassword, formData.newPassword);
      setSuccess(true);
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);
    } catch (err: any) {
      console.error('Failed to change password:', err);
      const errorMessage = err.response?.data?.detail || err.detail || err.message || 
        t('auth.changePassword.error', 'Failed to change password. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ m: 1, bgcolor: 'success.main' }}>
                  <LockOutlined />
                </Avatar>
                <Typography component="h1" variant="h5">
                  {t('auth.changePassword.success', 'Password Changed Successfully!')}
                </Typography>
              </Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t(
                  'auth.changePassword.successMessage',
                  'Your password has been changed successfully. Redirecting...'
                )}
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                <LockOutlined />
              </Avatar>
              <Typography component="h1" variant="h5">
                {t('auth.changePassword.title', 'Change Password')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                {t('auth.changePassword.subtitle', 'Enter your current password and choose a new one')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="currentPassword"
                label={t('auth.changePassword.currentPassword', 'Current Password')}
                type="password"
                id="currentPassword"
                autoComplete="current-password"
                value={formData.currentPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label={t('auth.changePassword.newPassword', 'New Password')}
                type="password"
                id="newPassword"
                autoComplete="new-password"
                value={formData.newPassword}
                onChange={handleInputChange}
                disabled={loading}
                helperText={t('auth.changePassword.passwordHint', 'Minimum 6 characters')}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('auth.changePassword.confirmPassword', 'Confirm New Password')}
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('auth.changePassword.change', 'Change Password')
                )}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/dashboard')}
                disabled={loading}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ChangePassword;






